/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, memo } from 'react';

import { breakpoints } from '@coursera/cds-core';
import type { Recommender } from '@coursera/event-pulse-types';

import { getShouldShowUnregisteredOnboarding } from 'bundles/coursera-plus/utils/subscriptionsForwardDiscoveryExperiment';
import InlineLearningGoalQuestion from 'bundles/onboarding-common/components/InlineLearningGoalQuestion';
import SearchPageMerchandisingCollection from 'bundles/search-common/components/experiments/merchInSearch/SearchPageMerchandisingCollection';
import { getGoogleAIRefreshProducts } from 'bundles/search-common/components/experiments/merchInSearch/merchandisingInSearchUtils';
import SearchCards from 'bundles/search-common/components/search-cards/SearchCards';
import { SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING } from 'bundles/search-common/constants';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';
import {
  getShouldShowGoogleAISpotlightCollectionsInSearch,
  getShouldShowGoogleAISpotlightCollectionsInSearchWithImpression,
} from 'bundles/search-common/utils/experimentUtils';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';

const styles = {
  inlineLearningGoalQuestionWrapper: css`
    padding: var(--cds-spacing-300) var(--cds-spacing-100) var(--cds-spacing-600);
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-50) var(--cds-spacing-300);
    }
  `,
};

type SearchPageBodySectionsProps = {
  hits: Array<SearchHit | SearchUpsellHit>;
  resultsPerPage: number;
  forceOpenInNewTab: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  isLoading?: boolean;
  isSearchPage: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
  cardIndexOffset?: number;
  isFirstSection?: boolean;
  page?: number;
  isSeoEntityPagePath?: boolean;
};

const SearchPageBodySections = ({
  hits,
  resultsPerPage,
  forceOpenInNewTab,
  eventingData,
  isLoading = false,
  isSearchPage,
  onScrolledToBottom,
  totalHitsDisplayed,
  cardIndexOffset,
  isFirstSection,
  page,
  isSeoEntityPagePath,
}: SearchPageBodySectionsProps) => {
  const shouldShowMerchandising = getShouldShowGoogleAISpotlightCollectionsInSearch();

  if (!hits?.length || !Array.isArray(hits)) return null;

  const searchHits = hits.filter((hit) => Array.isArray(hit) && isSearchProductHit(hit));
  const googleAIRefreshProducts = getGoogleAIRefreshProducts(searchHits);
  if (googleAIRefreshProducts && googleAIRefreshProducts?.length > 0 && isSearchPage) {
    getShouldShowGoogleAISpotlightCollectionsInSearchWithImpression();
  }

  const sections: Array<Array<SearchHit | SearchUpsellHit>> = [];
  for (let i = 0; i < hits.length; i += resultsPerPage) {
    sections.push(hits.slice(i, i + resultsPerPage));
  }

  const renderSection = (section: Array<SearchHit | SearchUpsellHit>, index: number) => {
    const sectionOffset = cardIndexOffset ? cardIndexOffset + index * resultsPerPage : undefined;
    const showInlineOnboarding =
      getShouldShowUnregisteredOnboarding() && ((page === 1 && isSeoEntityPagePath) || isFirstSection);
    if (showInlineOnboarding) {
      const firstHalf = section.slice(0, SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING);
      const secondHalf = section.slice(SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING);

      return (
        <Fragment>
          <SearchCards
            hits={firstHalf}
            resultsPerPage={resultsPerPage}
            forceOpenInNewTab={forceOpenInNewTab}
            eventingData={eventingData}
            isLoading={isLoading}
            isSearchPage={isSearchPage}
            onScrolledToBottom={onScrolledToBottom}
            totalHitsDisplayed={totalHitsDisplayed}
            cardIndexOffset={sectionOffset}
          />
          <div css={styles.inlineLearningGoalQuestionWrapper}>
            <InlineLearningGoalQuestion isCompact />
          </div>
          <SearchCards
            hits={secondHalf}
            resultsPerPage={resultsPerPage}
            forceOpenInNewTab={forceOpenInNewTab}
            eventingData={eventingData}
            isLoading={isLoading}
            isSearchPage={isSearchPage}
            onScrolledToBottom={onScrolledToBottom}
            totalHitsDisplayed={totalHitsDisplayed}
            cardIndexOffset={
              sectionOffset
                ? sectionOffset + SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING
                : SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING
            }
            isEndOfSplitSection={true}
          />
        </Fragment>
      );
    }

    return (
      <SearchCards
        hits={section}
        resultsPerPage={resultsPerPage}
        forceOpenInNewTab={forceOpenInNewTab}
        eventingData={eventingData}
        isLoading={isLoading}
        isSearchPage={isSearchPage}
        onScrolledToBottom={onScrolledToBottom}
        totalHitsDisplayed={totalHitsDisplayed}
        cardIndexOffset={sectionOffset}
      />
    );
  };

  return (
    <div>
      {sections.map((section, index) => (
        <div key={`section-${section[0].id}`}>
          {renderSection(section, index)}
          {shouldShowMerchandising && isSearchPage && (
            <SearchPageMerchandisingCollection
              hits={section}
              isLoading={isLoading}
              eventingData={eventingData}
              forceOpenInNewTab={forceOpenInNewTab}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default memo(SearchPageBodySections);
